import {
    activityCategoryLogLabels,
    buildDurationOptions,
    CALENDAR_EVENT_TIME_GRANULARITY_MINUTES,
    CalendarDate,
    CalendarEventId,
    CalendarEventType,
    DEFAULT_CALENDAR_EVENT_DURATION_MINUTES,
    durationOptionsMinutes,
    formatDuration,
    genEntityId,
    getCurrentTimezone,
    HomeCareLeadId,
    ReferenceBusinessId,
    ReferenceContactId,
    TaskTypeId,
    USER_TEXT_MAX_LENGTH,
    UserId,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AddressDisplay } from "components/contact-display/address-display";
import { trpc } from "components/rpc/trpc";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import {
    activityTypeChoicesForRecordType,
    chooseActivityTypeLabel,
    noActivityTypeKey,
    useActivityTypeOptions,
} from "components/tasks/activity-type-options";
import { DeleteButton } from "components/ui/action-button";
import { DatePicker } from "components/ui/date-time-picker/date-picker";
import { TimePicker } from "components/ui/date-time-picker/time-picker";
import { DictationTextArea } from "components/ui/dictation-textarea";
import {
    CommunityLink,
    HomeCareLeadLink,
    ReferenceBusinessLink,
    ReferenceContactLink,
} from "components/ui/entity-link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { SpMultiSelect } from "components/ui/sp-multi-select";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import { useDictationSaveTracker } from "hooks/use-dictation-save-tracker";
import { produce } from "immer";
import React, { useCallback, useMemo, useReducer, useState } from "react";
import { useCurrentUserId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import {
    attendeeCandidateOptions,
    attendeeCandidatesQueryOptions,
    attendeeLabel,
    attendeePlaceholder,
} from "./attendee-candidates";
import {
    activityRecordTypeForActivityParentKind,
    CalendarEventDto,
    visitAddress,
    VisitParent,
} from "./types";

type VisitEditorProps =
    | {
          mode: "edit";
          visit: CalendarEventDto;
          onSaved: () => void;
          onClose: () => void;
      }
    | {
          mode: "new";
          parent: VisitParent;
          activityTypeId: TaskTypeId | null;
          onSaved: () => void;
          onClose: () => void;
      }
    | {
          mode: "complete";
          parent: VisitParent;
          activityTypeId: TaskTypeId | null;
          onSaved: () => void;
          onClose: () => void;
      };

interface ComponentState {
    status: "loaded" | "saving" | "failed";
    visit: CalendarEventDto;
    visitExists: boolean;
    errorMessage: string;
}

type Action =
    | { type: "startSaving" }
    | { type: "setError"; message: string }
    | { type: "updateField"; field: keyof CalendarEventDto; value: string | null };

const defaultErrorMessage = "The visit failed to save.";

const reducer: React.Reducer<ComponentState, Action> = (p, a): ComponentState => {
    return produce(p, draft => {
        switch (a.type) {
            case "startSaving": {
                draft.status = "saving";
                draft.errorMessage = "";
                break;
            }
            case "setError": {
                draft.status = "failed";
                draft.errorMessage = a.message || defaultErrorMessage;
                break;
            }
            case "updateField": {
                if (a.field === "eventDuration") {
                    draft.visit.eventDuration = Number(a.value);
                } else {
                    (draft.visit as Record<string, unknown>)[a.field] = a.value;
                }
                break;
            }
        }
    });
};

const pad = (value: number): string => String(value).padStart(2, "0");

const timeOnTheGranularityGrid = (now: Date): string => {
    const minutes =
        Math.floor(now.getMinutes() / CALENDAR_EVENT_TIME_GRANULARITY_MINUTES) *
        CALENDAR_EVENT_TIME_GRANULARITY_MINUTES;
    return `${pad(now.getHours())}:${pad(minutes)}`;
};

type VisitParentFields = Pick<
    CalendarEventDto,
    | "parentKind"
    | "businessId"
    | "businessName"
    | "businessAddress"
    | "parentContactName"
    | "clientName"
    | "clientAddress"
>;

const parentFieldsFor = (parent: VisitParent): VisitParentFields => {
    switch (parent.kind) {
        case "organization":
            return {
                parentKind: parent.kind,
                businessId: parent.id,
                businessName: parent.name,
                businessAddress: parent.address,
                parentContactName: null,
                clientName: null,
                clientAddress: null,
            };
        case "contact":
            return {
                parentKind: parent.kind,
                businessId: null,
                businessName: null,
                businessAddress: parent.address,
                parentContactName: parent.name,
                clientName: null,
                clientAddress: null,
            };
        case "homeCareLead":
            return {
                parentKind: parent.kind,
                businessId: null,
                businessName: null,
                businessAddress: null,
                parentContactName: null,
                clientName: parent.name,
                clientAddress: parent.address,
            };
    }
    const exhaustiveCheck: never = parent;
    throw new Error(`Unhandled activity parent ${String(exhaustiveCheck)}`);
};

const emptyVisit = (
    parent: VisitParent,
    userId: UserId,
    activityTypeId: TaskTypeId | null,
    eventTime: string | null,
): CalendarEventDto => {
    const now = new Date();
    const todayDate = CalendarDate.fromLocalDate(now);
    return {
        id: genEntityId<CalendarEventId>(),
        parentId: parent.id,
        userId,
        type: CalendarEventType.Visit,
        eventDate: todayDate.toString(),
        eventTime,
        eventDuration: DEFAULT_CALENDAR_EVENT_DURATION_MINUTES,
        eventTimeZone: null,
        title: null,
        privateNotes: null,
        outcome: null,
        activityTypeId,
        communityId: null,
        communityName: null,
        communityAddress: null,
        dateAdded: now.toISOString(),
        updatedAt: now.toISOString(),
        involvedContactIds: [],
        ...parentFieldsFor(parent),
    };
};

const initialStateForExisting = (visit: CalendarEventDto): ComponentState => {
    return {
        status: "loaded",
        visit,
        visitExists: true,
        errorMessage: "",
    };
};

const initialStateForScheduled = (
    parent: VisitParent,
    userId: UserId,
    activityTypeId: TaskTypeId | null,
): ComponentState => {
    return {
        status: "loaded",
        visit: emptyVisit(parent, userId, activityTypeId, null),
        visitExists: false,
        errorMessage: "",
    };
};

const initialStateForCompleted = (
    parent: VisitParent,
    userId: UserId,
    activityTypeId: TaskTypeId | null,
): ComponentState => {
    return {
        status: "loaded",
        visit: emptyVisit(
            parent,
            userId,
            activityTypeId,
            timeOnTheGranularityGrid(new Date()),
        ),
        visitExists: false,
        errorMessage: "",
    };
};

const VisitClientLink: React.FC<{ visit: CalendarEventDto }> = ({ visit }) => {
    if (!visit.clientName) {
        return null;
    }
    switch (visit.parentKind) {
        case "homeCareLead":
            return (
                <div>
                    <HomeCareLeadLink
                        homeCareLeadId={visit.parentId as HomeCareLeadId}
                        name={visit.clientName}
                    />
                </div>
            );
        case "organization":
        case "contact":
            return null;
    }
    const exhaustiveCheck: never = visit.parentKind;
    throw new Error(`Unhandled visit parent kind ${String(exhaustiveCheck)}`);
};

export const VisitEditor: React.FC<VisitEditorProps> = props => {
    const { onSaved, onClose } = props;
    const currentUserId = useCurrentUserId();
    const openedAsExisting = props.mode === "edit";
    const isCompleteMode = props.mode === "complete";

    const [state, dispatch] = useReducer(
        reducer,
        props.mode === "edit"
            ? initialStateForExisting(props.visit)
            : props.mode === "complete"
              ? initialStateForCompleted(
                    props.parent,
                    currentUserId,
                    props.activityTypeId,
                )
              : initialStateForScheduled(
                    props.parent,
                    currentUserId,
                    props.activityTypeId,
                ),
    );

    const parentKind = state.visit.parentKind;
    const [involvedContactIds, setInvolvedContactIds] = useState<ReferenceContactId[]>(
        state.visit.involvedContactIds,
    );

    const activityTypes = useActivityTypeOptions();
    const visitTypeChoices = activityTypeChoicesForRecordType(
        activityTypes,
        "visit",
        activityRecordTypeForActivityParentKind(parentKind),
        state.visit.activityTypeId,
    );

    const currentDurationMinutes = state.visit.eventDuration;
    const durationOptions = buildDurationOptions(
        durationOptionsMinutes,
        currentDurationMinutes,
    );

    const trpcClient = trpc.useTRPC();

    const attendeeCandidatesQuery = useQuery(
        attendeeCandidatesQueryOptions(
            trpcClient,
            parentKind === "organization"
                ? (state.visit.parentId as ReferenceBusinessId)
                : null,
        ),
    );
    const attendeeOptions = useMemo(
        () => attendeeCandidateOptions(attendeeCandidatesQuery.data),
        [attendeeCandidatesQuery.data],
    );

    const onSaveSuccess = useCallback(() => {
        onSaved();
        onClose();
    }, [onSaved, onClose]);

    const onSaveError = useCallback(
        (error: { message?: string }) => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
        [dispatch],
    );

    const onCreateSuccess = useCallback(() => {
        handleEvent("visit-save", { parentKind });
        onSaveSuccess();
    }, [onSaveSuccess, parentKind]);

    const onUpdateSuccess = useCallback(() => {
        if (state.visit.outcome?.trim()) {
            handleEvent("visit-outcome-recorded", { parentKind });
        }
        onSaveSuccess();
    }, [onSaveSuccess, parentKind, state.visit.outcome]);

    const createMutation = useMutation({
        ...trpcClient.calendarEvents.create.mutationOptions(),
        onSuccess: onCreateSuccess,
        onError: onSaveError,
    });

    const updateMutation = useMutation({
        ...trpcClient.calendarEvents.update.mutationOptions(),
        onSuccess: onUpdateSuccess,
        onError: onSaveError,
    });

    const deleteMutation = useMutation({
        ...trpcClient.calendarEvents.delete.mutationOptions(),
        onSuccess: () => {
            onSaved();
            onClose();
        },
        onError: onSaveError,
    });

    const handleFieldChange = useCallback(
        (field: keyof CalendarEventDto, value: string | null) => {
            dispatch({ type: "updateField", field, value });
        },
        [dispatch],
    );

    const dictationSaveTracker = useDictationSaveTracker("visit-outcome");
    const outcomeRequired = isCompleteMode;
    const attendeesCanBeSaved = isCompleteMode || !!state.visit.outcome?.trim();

    const handleSave = useCallback(() => {
        if (!state.visit.eventDate) {
            dispatch({
                type: "setError",
                message: "Please choose a date for the visit.",
            });
            return;
        }
        if (outcomeRequired && !state.visit.outcome?.trim()) {
            dispatch({
                type: "setError",
                message: "Please add an outcome to complete this visit.",
            });
            return;
        }
        dispatch({ type: "startSaving" });
        dictationSaveTracker.recordSaved(state.visit.outcome ?? "");
        const eventTimeZone = getCurrentTimezone();
        if (state.visitExists) {
            updateMutation.mutate({
                id: state.visit.id,
                userId: state.visit.userId,
                eventDate: state.visit.eventDate,
                eventTime: state.visit.eventTime,
                eventDuration: state.visit.eventDuration,
                eventTimeZone,
                title: state.visit.title,
                privateNotes: state.visit.privateNotes,
                outcome: state.visit.outcome,
                activityTypeId: state.visit.activityTypeId,
                involvedContactIds:
                    parentKind === "organization" ? involvedContactIds : [],
            });
        } else {
            createMutation.mutate({
                parentId: state.visit.parentId,
                userId: state.visit.userId,
                eventDate: state.visit.eventDate,
                eventTime: state.visit.eventTime,
                eventDuration: state.visit.eventDuration,
                eventTimeZone,
                title: state.visit.title,
                privateNotes: state.visit.privateNotes,
                outcome: state.visit.outcome,
                activityTypeId: state.visit.activityTypeId,
                involvedContactIds:
                    parentKind === "organization" ? involvedContactIds : [],
            });
        }
    }, [
        state.visit,
        state.visitExists,
        outcomeRequired,
        createMutation,
        updateMutation,
        parentKind,
        involvedContactIds,
        dictationSaveTracker,
    ]);

    const handleDelete = useCallback(() => {
        if (state.visitExists) {
            deleteMutation.mutate({ id: state.visit.id });
        }
    }, [state.visit.id, state.visitExists, deleteMutation]);

    const isSaving = state.status === "saving";

    return (
        <Panel type={PanelType.medium} headerText="" onDismiss={onClose} isOpen={true}>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                        {state.visitExists
                            ? "Edit Visit"
                            : isCompleteMode
                              ? activityCategoryLogLabels.visit
                              : "Add Visit"}
                    </h2>
                    {state.visitExists ? (
                        <DeleteButton
                            backgroundColor="bg-white"
                            onClick={handleDelete}
                            confirm={{
                                title: "Delete Visit",
                                message: "Are you sure you want to delete this visit?",
                            }}
                        />
                    ) : null}
                </div>
                <div className="space-y-1">
                    <VisitClientLink visit={state.visit} />
                    {state.visit.parentContactName ? (
                        <div>
                            <ReferenceContactLink
                                referenceContactId={
                                    state.visit.parentId as ReferenceContactId
                                }
                                name={state.visit.parentContactName}
                            />
                        </div>
                    ) : null}
                    {state.visit.businessId && state.visit.businessName ? (
                        <div>
                            <ReferenceBusinessLink
                                referenceBusinessId={state.visit.businessId}
                                name={state.visit.businessName}
                            />
                        </div>
                    ) : null}
                    {state.visit.communityId && state.visit.communityName ? (
                        <div>
                            <CommunityLink
                                communityId={state.visit.communityId}
                                name={state.visit.communityName}
                            />
                        </div>
                    ) : null}
                    {visitAddress(state.visit) ? (
                        <div className="text-sm">
                            <AddressDisplay value={visitAddress(state.visit)} />
                        </div>
                    ) : null}
                </div>
                {visitTypeChoices.length > 0 ? (
                    <Select
                        label="Visit type"
                        value={state.visit.activityTypeId ?? noActivityTypeKey}
                        onChange={e =>
                            handleFieldChange(
                                "activityTypeId",
                                e.target.value === noActivityTypeKey
                                    ? null
                                    : e.target.value,
                            )
                        }>
                        {state.visit.activityTypeId ? null : (
                            <option value={noActivityTypeKey}>
                                {chooseActivityTypeLabel}
                            </option>
                        )}
                        {visitTypeChoices.map(choice => (
                            <option key={choice.id} value={choice.id}>
                                {choice.name}
                            </option>
                        ))}
                    </Select>
                ) : null}
                <TextArea
                    label="Description"
                    rows={3}
                    value={state.visit.title || ""}
                    onChange={e => handleFieldChange("title", e.target.value || null)}
                />

                <div>
                    <DatePicker
                        label="Date"
                        value={state.visit.eventDate || null}
                        onChange={value => {
                            if (value) {
                                handleFieldChange("eventDate", value);
                            }
                        }}
                        disabled={false}
                        required={true}
                        textSize="base"
                        onOpenChange={() => undefined}
                        minValue={null}
                        maxValue={null}
                        visibleYears={20}
                    />
                </div>

                <div>
                    <TimePicker
                        label="Time (optional)"
                        value={state.visit.eventTime || null}
                        onChange={value => handleFieldChange("eventTime", value)}
                        disabled={false}
                        nativeStepMinutes={CALENDAR_EVENT_TIME_GRANULARITY_MINUTES}
                    />
                </div>

                {state.visit.eventTime ? (
                    <div>
                        <Select
                            label="Duration"
                            value={String(currentDurationMinutes)}
                            onChange={e =>
                                handleFieldChange("eventDuration", e.target.value)
                            }>
                            {durationOptions.map(minutes => (
                                <option key={minutes} value={String(minutes)}>
                                    {formatDuration(minutes)}
                                </option>
                            ))}
                        </Select>
                    </div>
                ) : null}

                <UserSelect
                    label="Assigned to"
                    value={state.visit.userId as SelectableUser}
                    includeEveryone={false}
                    onChange={userId => {
                        if (userId) {
                            handleFieldChange("userId", userId as string);
                        }
                    }}
                />

                {openedAsExisting || isCompleteMode ? (
                    <DictationTextArea
                        label={outcomeRequired ? "Outcome (required)" : "Outcome"}
                        rows={3}
                        value={state.visit.outcome || ""}
                        placeholder="Recap of what happened during the visit"
                        onValueChange={value =>
                            handleFieldChange("outcome", value || null)
                        }
                        onDictationEnd={dictationSaveTracker.recordDictated}
                        source="visit-outcome"
                        characterLimit={USER_TEXT_MAX_LENGTH}
                    />
                ) : null}

                {attendeesCanBeSaved && parentKind === "organization" ? (
                    <SpMultiSelect
                        label={attendeeLabel}
                        options={attendeeOptions}
                        value={involvedContactIds}
                        onChange={setInvolvedContactIds}
                        placeholder={attendeePlaceholder(attendeeOptions)}
                    />
                ) : null}

                {state.errorMessage ? (
                    <div className="text-red-600 text-sm">{state.errorMessage}</div>
                ) : null}

                <div className="flex justify-end items-center gap-2 pt-4">
                    {isSaving ? <Spinner /> : null}
                    <SecondaryButton onClick={onClose} disabled={isSaving}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={handleSave} disabled={isSaving}>
                        {outcomeRequired ? "Save & complete" : "Save"}
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};
